import { KeenIcon, Menu, MenuItem, MenuToggle } from '@/components';
import { useLanguage } from '@/i18n';
import { DropdownCard2 } from '@/partials/dropdowns/general';
import { CommonHexagonBadge } from '@/partials/common';
import authService from '@/services/auth/authService';
import { toast } from 'sonner';
import { useState } from 'react';
import { useAuthContext } from '@/auth';
import { OTPModal } from '@/components/modal'
import { setupCodeRejection } from '@/auth/twoFactorSetup';

const AuthTwoFactor = () => {
  const { isRTL } = useLanguage();
  const [qrCode, setQrCode] = useState(null);
  const [seceret, setSecret] = useState(null);
  const [qrCodeError, setQrCodeError] = useState(null);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [DisableModalOpen, setDisableModalOpen] = useState(false);
  const { auth, updateUser } = useAuthContext();
  const is2FaEnabled = auth?.user?.is_2fa_enabled == 1;

  const items = [ {
    icon: is2FaEnabled ? 'shield-tick' : 'shield-cross',
    title: 'Authenticator App',
    description: 'Elevate protection with an authenticator app for two-factor authentication.',
    checkbox: false
  }];
  

  const enable2fa = async () => {
    setModalOpen(true);
    setIsLoading(true);
    setQrCode(null);
    setSecret(null);
    setQrCodeError(null);
    try {
      const response = await authService.enable2fa();

      if (response && response.data.qr_code) {
        setQrCode(response.data.qr_code)
        setSecret(response.data.secret)
      } else {
        toast.error('Failed to fetch QR code.', {
          description: 'An error occurred while enabling 2FA. Please try again later.',
          
        });
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error?.error || error?.message || 'Something went wrong.';

      if (errorMessage === 'Too Many Attempts.') {
        setQrCodeError(errorMessage);

        toast.warning('Verification Unavailable', {
          description: 'Please wait a minute and try again.',
          
        });
      } else {
        setQrCodeError('An unexpected error occurred.');

        toast.error('Connection Error', {
          description: 'An error occurred while enabling 2FA. Please try again later.',
          
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const disable2fa = () => {
    setDisableModalOpen(true);
  };
  
  const handleSubmitOTP = async () => {
    setIsLoading(true);
    try {
      const response = await authService.verify2faOTP({otp: otp});
      if (response.status) {
        const user = response.data.user;
        const updatedUser = {
          is_2fa_enabled: 1
        }
        updateUser(updatedUser, true)
        setModalOpen(false)
        setOtp('')
        toast.success('2FA Enabled successfully', {
          description: 'Two-Factor Authentication has been successfully enabled.',
        })
      }
    } catch (error) {
      console.error('Error:', error.message);
      const rejection = setupCodeRejection(error.message);
      if (rejection) {
        setOtp('')
        toast.warning('Code not accepted', { description: rejection, duration: 15000 });
        return
      }
      toast.error('Error Verifying OTP', {
        description: 'Failed to verify OTP. Please try again.',
        
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitDisable2fa = async () => {
    setIsLoading(true);
    try {
      const response = await authService.disableVerify2faOTP({otp: otp});
      if (response.status) {
        const user = response.data.user;
        const updatedUser = {
          is_2fa_enabled: 0
        }
        updateUser(updatedUser, true)
        setOtp('')
        toast.success('2FA Disabled successfully', {
          description: 'Two-Factor Authentication has been successfully disabled.',
        })
      }
    } catch (error) {
      console.error('Error:', error.message);
      toast.error('Error Verifying OTP', {
        description: 'Failed to verify OTP. Please try again.',
        
      });
    } finally {
      setDisableModalOpen(false)
      setIsLoading(false);
      setOtp('')
    }
  };
    
  const handleCloseModal = () => {
    setModalOpen(false)
    setOtp('')
  }

  const handleCloseDisableModal = () => {
    setDisableModalOpen(false)
    setOtp('')
  }

  const renderItem = (item, index) => {
    return <div key={index} className="flex items-center justify-between flex-wrap border border-gray-200 rounded-xl gap-2 px-3.5 py-2.5">
        <div className="flex items-center flex-wrap gap-3.5">
          <div className="flex items-center">
            <CommonHexagonBadge stroke="stroke-gray-300" fill={`${is2FaEnabled ? 'fill-green-300 opacity-10' : 'fill-gray-100'}`} size="size-[50px]" badge={<KeenIcon icon={item.icon} className={`text-xl ${is2FaEnabled ? 'text-green-500' : 'text-gray-500'}`} />} />
          </div>

          <div className="flex flex-col gap-1">
            <span className={`badge badge-outline badge-xs w-fit ${is2FaEnabled ? 'badge-success' : 'badge-light'}`}>{is2FaEnabled ? 'Enabled' : 'Disabled'}</span>
            <span className="text-sm font-medium text-gray-900">
              {item.title}
            </span>
            <span className="text-2sm font-medium text-gray-700">{item.description}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 lg:gap-6">
        {auth?.user?.is_2fa_enabled == 1 &&
          <button onClick={disable2fa} className="btn btn-link btn-sm">
            Disable
          </button>
        }
        {(auth?.user?.is_2fa_enabled == 0 || auth?.user?.is_2fa_enabled == null)  &&
          <button onClick={enable2fa} className="btn btn-link btn-sm">
            Setup
          </button>
        }
        </div>
      </div>;
  };
  return (
    <>
      <div className="card">
        <div className="card-header" id="auth_two_factor">
          <h3 className="card-title">Two-Factor authentication(2FA)</h3>

          <Menu className="items-stretch">
            <MenuItem toggle="dropdown" trigger="click" dropdownProps={{
            placement: isRTL() ? 'bottom-start' : 'bottom-end',
            modifiers: [{
              name: 'offset',
              options: {
                offset: isRTL() ? [0, -10] : [0, 10] // [skid, distance]
              }
            }]
          }}>
              {/* <MenuToggle className="btn btn-sm btn-icon btn-light btn-clear">
                <KeenIcon icon="dots-vertical" />
              </MenuToggle> */}
              {DropdownCard2()}
            </MenuItem>
          </Menu>
        </div>

        <div className="card-body">
          <div className="grid gap-5">
            {items.map((item, index) => {
            return renderItem(item, index);
          })}
          </div>
        </div>
      </div>
      {/* Enable 2FA */}
      <OTPModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitOTP}
        otp={otp}
        setOtp={setOtp}
        isLoading={isLoading}
        title="Choose An Authentication Method"
        description={
          <>
            <h3 className="text-lg font-medium text-gray-900">Authenticator Apps</h3>
            <p className="text-sm">
            Using an authenticator app like <a href="https://support.google.com/accounts/answer/1066447?hl=en" target='_blank' className='link'>Google Authenticator</a>, <a href="https://www.microsoft.com/en-us/security/mobile-authenticator-app" target='_blank' className='link'>Microsoft Authenticator</a>, <a href="https://www.authy.com/download/" target='_blank' className='link'>Authy</a>, or <a href="https://support.1password.com/one-time-passwords/?windows" target='_blank' className='link'>1Password</a>, scan the QR code. It will generate a 6 digit code for you to enter below.
            </p>
          </>
        }
        qrCode={qrCode}
        qrCodeError={qrCodeError}
        secret={seceret}
        disable2FA={false}
      />
      {/* Disable 2FA */}
      <OTPModal
        open={DisableModalOpen}
        onClose={handleCloseDisableModal}
        onSubmit={handleSubmitDisable2fa}
        otp={otp}
        setOtp={setOtp}
        isLoading={isLoading}
        title="Disable Two Factor Authentication"
        description={
          <>
            <h3 className="text-lg font-medium text-gray-900">Authenticator Apps</h3>
            <p className="text-sm">
            Using an authenticator app like <a href="https://support.google.com/accounts/answer/1066447?hl=en" target='_blank' className='link'>Google Authenticator</a>, <a href="https://www.microsoft.com/en-us/security/mobile-authenticator-app" target='_blank' className='link'>Microsoft Authenticator</a>, <a href="https://www.authy.com/download/" target='_blank' className='link'>Authy</a>, or <a href="https://support.1password.com/one-time-passwords/?windows" target='_blank' className='link'>1Password</a>, scan the QR code. It will generate a 6 digit code for you to enter below.
            </p>
          </>
        }
        disable2FA={true}
      />
    </>
  ) 
};
export { AuthTwoFactor };